import React, { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AppleSmall } from '@wix/wix-ui-icons-common';
import { DH } from '../../../utils/dataHooks';
import { type Locale } from '../../../types/Locale';
import { type Shape } from '../../../types/Styles';
import { Experiment } from '../../../types/Experiment';
import { selectExperiments } from '../../../selectors';
import { st, classes } from './styles.st.css';

interface ApplePayProps {
  height: number;
  locale: Locale;
  color: 'black' | 'white' | 'white-outline';
  shape: Shape;
  onClick(): void;
}

export const ApplePayView = (props: ApplePayProps) => {
  const { locale, color, shape, height, onClick } = props;
  const experiments = useSelector(selectExperiments);
  const applePayButtonRef = useRef<HTMLElement | null>(null);
  const onClickRef = useRef(onClick);

  const applePayButtonStyles = useMemo(
    () =>
      ({
        '--apple-pay-button-height': `${height}px`,
      } as React.CSSProperties),
    [height],
  );

  const buttonHeightStyle = useMemo(
    () => ({
      height: `${height}px`,
    }),
    [height],
  );

  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  useEffect(() => {
    const buttonElement = applePayButtonRef.current;
    if (buttonElement) {
      const handleClick = () => {
        onClickRef.current();
      };

      buttonElement.addEventListener('click', handleClick);
      return () => {
        buttonElement.removeEventListener('click', handleClick);
      };
    }
  }, []);

  if (experiments[Experiment.EnableApplePayInChrome] === 'true') {
    return (
      <section
        data-hook="apple-pay-view"
        className={st(classes.applePayButton, classes[shape])}
        style={buttonHeightStyle}
      >
        <apple-pay-button
          ref={applePayButtonRef}
          buttonstyle={color}
          type="check-out"
          locale={locale}
          style={applePayButtonStyles}
          data-hook={DH.ApplePay.Button}
        />
      </section>
    );
  }

  return (
    <section data-hook="apple-pay-view">
      <button
        lang={locale}
        data-hook={DH.ApplePay.Button}
        style={buttonHeightStyle}
        className={st(classes.root, classes[color], classes[shape])}
        onClick={onClick}
      >
        <span className={classes.text}>
          Check out with <AppleSmall />
          Pay
        </span>
      </button>
    </section>
  );
};
